import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

// Family context: powers the Netflix-style profile chooser + persistent
// switcher on the student app. A parent with multiple linked kids
// (`guardian_email` on the child rows OR shared `email`) sees a picker
// on first sign-in; subsequent visits use the last chosen kid via the
// session-scoped `active_student_id`.
const FamilyCtx = createContext(null);

export function FamilyProvider({ enabled, children }) {
  const [members, setMembers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/student/family");
      setMembers(data.members || []);
      setActiveId(data.active_student_id || null);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  const switchTo = useCallback(async (student_id) => {
    const { data } = await api.post("/student/switch", { student_id });
    setActiveId(data.active_student_id);
    return data;
  }, []);

  // Auto-pick when there's exactly one profile — no chooser needed.
  useEffect(() => {
    if (!enabled) return;
    if (!loading && members.length === 1 && !activeId) {
      switchTo(members[0].student_id).catch(() => {});
    }
  }, [enabled, loading, members, activeId, switchTo]);

  return (
    <FamilyCtx.Provider value={{ members, activeId, loading, error, refresh, switchTo }}>
      {children}
    </FamilyCtx.Provider>
  );
}

export const useFamily = () => useContext(FamilyCtx) || {
  members: [], activeId: null, loading: false, error: null,
  refresh: () => {}, switchTo: async () => {},
};
